/**
 * visitReportDeadLetter — the DEAD-LETTER answer of both visit-report doors, as the FE
 * CONSUMER expects it (a declared expectation, not a hand-written producer schema).
 *
 * WHAT THE PRODUCER DOES (read in the resolver body, migration
 * 20261004213847_SL_account_ref_resolver_at_both_submit_doors, RUNG 4): when
 * core.fn_resolve_visit_account_ref(p_unified_lead_id) resolves NOTHING, both doors write
 * one core.projection_dead_letter row (reason_code 'lead_unresolvable', the full payload)
 * and RETURN SUCCESS with {engagement_id: null, dead_lettered: true, reason_code,
 * source_event_id} — no RAISE, no attributed row. raider-intel's v8 answers door
 * (public.rpc_submit_visit_v8_answers, DESIGN (d) "Response") adds answers_dead_lettered:
 * true / answers_projection / answers_dead_letter_submits.
 *
 * THE GENERATED RESPONSE MODELS DO NOT CARRY IT YET (VisitReportV2Engagement /
 * VisitReportV8Result require a non-null engagement_id). Until SL's regenerated OpenAPI
 * lands, this module is the consumer's declared expectation: BOTH pacts render
 * DEAD_LETTER_EXPECTATION into a dead-letter interaction and pin it as an expected-fail
 * against the generated response schema (red the day codegen carries it → re-bind to the
 * generated type and delete this declaration).
 *
 * WHAT THE FE DOES WITH IT: a dead letter is ACCEPTED-TERMINAL — the server holds the
 * report and will replay it once the lead resolves. No rep error, the outbox entry is
 * pruned, the draft is cleared; telemetry carries the key + reason code.
 */

import { VisitReportV8DeadLetter as VisitReportV8DeadLetterSchema } from '../wire/generated/schemas.generated'

/**
 * The declared consumer expectation, per door.
 *
 * v8: keys of the door's `data` object mark it (the flag, the reason, the key, a null engagement).
 *
 * v2: the dead letter is NOT inside `data`. The door answers HTTP 200 with `dead_letter` as a
 * TOP-LEVEL SIBLING of `data`, and no `data` key at all:
 *   {"success":true,"dead_letter":{"reason_code":"lead_unresolvable","source_event_id":"<key>"}}
 * (service layer, executed through the real route at staging fc09a34a on 2026-10-09; on the wire
 * since service-layer 6e5360501, PR #2698). Until 2026-10-09 this module looked for the v2 dead
 * letter inside `data` under a `dead_lettered` flag — a shape the v2 door never sent — so every
 * real v2 dead letter was read as a contract breach and the report marked failed for good
 * (BUG-FE-V2-DEAD-LETTER-TOP-LEVEL-SIBLING-READ-AS-CONTRACT-BREACH-2026-10-09). Hand-typed and
 * narrow on purpose; the openapi re-mirror binds it to the generated VisitReportV2SubmitResponse.
 */
export const DEAD_LETTER_EXPECTATION = {
  source:
    'resolver migration 20261004213847_SL_account_ref_resolver_at_both_submit_doors (RUNG 4, both doors) + '
    + 'raider-intel DESIGN (d) Response (public.rpc_submit_visit_v8_answers)',
  doors: {
    v2: { envelope: 'dead_letter', receipt: 'data', reason: 'reason_code', event: 'source_event_id' },
    v8: { flag: 'answers_dead_lettered', reason: 'reason_code', event: 'source_event_id', engagement: 'engagement_id' },
  },
} as const

export type DeadLetterDoor = keyof typeof DEAD_LETTER_EXPECTATION.doors

export interface VisitReportDeadLetter {
  kind: 'dead_lettered'
  door: DeadLetterDoor
  reasonCode: string
  sourceEventId: string
}

/** A door said "dead-lettered" but not in the declared shape — never read as accepted. Names EVERY declared field
 *  the answer did not carry as declared (names only, never a value): the transport reports them (review F1). */
export class VisitReportDeadLetterShapeError extends Error {
  constructor(readonly door: DeadLetterDoor, readonly invalidFields: readonly string[]) {
    super(`visit-report ${door} dead-letter answer is malformed: ${invalidFields.join(', ')} not as declared`)
    this.name = 'VisitReportDeadLetterShapeError'
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v)

const isNonBlank = (v: unknown): v is string => typeof v === 'string' && v.trim() !== ''

/**
 * The v2 door's dead letter, read off the RESPONSE BODY (it sits beside `data`, never inside it).
 *
 * TOLERANT ON PURPOSE (FE coordinator ruling 2026-10-10): this reads "the server HOLDS your report", so it asks
 * only for a non-blank reason and key. It is deliberately NOT parsed through the generated VisitReportDeadLetter,
 * which pins reason_code to the one constant the producer has today: a new reason code must never turn a held
 * report into a failed one. An unknown code is acknowledged like any other and travels in the dead-letter record
 * (visitReportDeadLetterTelemetry), which is how a new code becomes visible. The generated model and this reader
 * are held to agree on today's body by test (visitReportDeadLetter.test.ts).
 */
function readV2DeadLetter(body: unknown): VisitReportDeadLetter | null {
  const keys = DEAD_LETTER_EXPECTATION.doors.v2
  const letter = isRecord(body) ? body[keys.envelope] : undefined
  if (letter === undefined || letter === null) return null
  if (!isRecord(letter) || !isRecord(body)) throw new VisitReportDeadLetterShapeError('v2', [keys.envelope])
  const reason = letter[keys.reason]
  const event = letter[keys.event]
  const invalid: string[] = []
  if (!isNonBlank(reason)) invalid.push(`${keys.envelope}.${keys.reason}`)
  if (!isNonBlank(event)) invalid.push(`${keys.envelope}.${keys.event}`)
  // "Exactly one of data and dead_letter": a body carrying both claims an attribution AND a hold.
  if (body[keys.receipt] !== null && body[keys.receipt] !== undefined) invalid.push(keys.receipt)
  if (invalid.length > 0 || !isNonBlank(reason) || !isNonBlank(event)) throw new VisitReportDeadLetterShapeError('v2', invalid)
  return { kind: 'dead_lettered', door: 'v2', reasonCode: reason, sourceEventId: event }
}

/**
 * A door's dead letter, or null when the answer is not one (the normal attributed answer — the
 * caller parses it). `answer` is the door's `data` object on v8 and the WHOLE response body on v2
 * (see DEAD_LETTER_EXPECTATION). THROWS when the answer says "dead letter" but not in the declared
 * shape: a half-shaped "accepted" is not proof the server holds the report, so it must not prune anything.
 */
export function readDeadLetter(door: DeadLetterDoor, answer: unknown): VisitReportDeadLetter | null {
  if (door === 'v2') return readV2DeadLetter(answer)
  // v8 — TOLERANT ON PURPOSE, the same principle as the v2 reader above (FE coordinator ruling 2026-10-10, review
  // finding on PR #2222): the PROOF that the server holds the report is the flag, a non-blank reason and the key
  // (held against the request's key by the transport, missionVisitReports.deadLetterOfThisWrite). The generated
  // VisitReportV8DeadLetter asks for more (answers_projection, the submit count, dead_lettered); a held report is
  // never turned into a failed one because one of those is missing — the difference is REPORTED (v8DeadLetterDrift).
  const data = answer
  const keys = DEAD_LETTER_EXPECTATION.doors.v8
  if (!isRecord(data) || data[keys.flag] !== true) return null
  const reason = data[keys.reason]
  const event = data[keys.event]
  const invalid: string[] = []
  if (typeof reason !== 'string' || reason.trim() === '') invalid.push(keys.reason)
  if (typeof event !== 'string' || event.trim() === '') invalid.push(keys.event)
  // A dead letter claims NO attribution; one that names an engagement is a contradiction.
  if (data[keys.engagement] !== null && data[keys.engagement] !== undefined) invalid.push(keys.engagement)
  if (invalid.length > 0 || typeof reason !== 'string' || typeof event !== 'string') throw new VisitReportDeadLetterShapeError(door, invalid)
  return { kind: 'dead_lettered', door, reasonCode: reason, sourceEventId: event }
}

/**
 * Where a v8 dead letter the reader ACCEPTED differs from the generated VisitReportV8DeadLetter: the fields the
 * model misses or refuses, and the keys it does not declare. Field NAMES only, never a value. Empty = no difference.
 */
export function v8DeadLetterDrift(data: unknown): { invalid: string[]; undeclared: string[] } {
  const parsed = VisitReportV8DeadLetterSchema.safeParse(data)
  const invalid = parsed.success ? [] : [...new Set(parsed.error.issues.map((i) => i.path.join('.') || '(root)'))]
  const undeclared = isRecord(data) ? Object.keys(data).filter((k) => !(k in VisitReportV8DeadLetterSchema.shape)) : []
  return { invalid, undeclared }
}
